import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Users, FileText, GraduationCap, MessageSquare,
  Plus, Bell, ArrowRight, TrendingUp,
} from 'lucide-react';
import { API_URL } from '@/config/api';

const AdminDashboard = () => {
  const [stats, setStats] = useState({ total_clients: 0, total_instructors: 0, total_blogs: 0, total_testimonials: 0 });
  const [recentClients, setRecentClients] = useState([]);
  const [activePopups, setActivePopups] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        const [statsRes, clientsRes, popupsRes] = await Promise.all([
          axios.get(`${API_URL}/stats`),
          axios.get(`${API_URL}/clients`),
          axios.get(`${API_URL}/popups`),
        ]);
        setStats(statsRes.data);
        // Latest 5 clients
        const sorted = [...(clientsRes.data || [])].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        setRecentClients(sorted.slice(0, 5));
        // Count active popups
        const now = new Date().toISOString();
        const active = (popupsRes.data || []).filter(p => {
          if (!p.enabled) return false;
          if (p.start_at && now < p.start_at) return false;
          if (p.end_at && now > p.end_at) return false;
          return true;
        });
        setActivePopups(active.length);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchAll();
  }, []);

  const statCards = [
    { label: '學員', value: stats.total_clients, icon: Users, color: 'text-blue-600', bg: 'bg-blue-50', link: '/admin/clients' },
    { label: '導師', value: stats.total_instructors, icon: GraduationCap, color: 'text-green-600', bg: 'bg-green-50', link: '/admin/instructors' },
    { label: '考試資料', value: stats.total_blogs, icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50', link: '/admin/blogs' },
    { label: '學員分享', value: stats.total_testimonials, icon: MessageSquare, color: 'text-orange-600', bg: 'bg-orange-50', link: '/admin/testimonials' },
    { label: 'Active 公告', value: activePopups, icon: Bell, color: 'text-pink-600', bg: 'bg-pink-50', link: '/admin/popups' },
  ];

  const quickActions = [
    { label: '新增學員', icon: Plus, path: '/admin/clients', color: 'bg-blue-500 hover:bg-blue-600' },
    { label: '新增公告', icon: Bell, path: '/admin/popups', color: 'bg-pink-500 hover:bg-pink-600' },
    { label: '新增考試資料', icon: FileText, path: '/admin/blogs', color: 'bg-purple-500 hover:bg-purple-600' },
  ];

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">儀表板</h1>
            <p className="text-muted-foreground text-sm">{new Date().toLocaleDateString('zh-HK', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4">
          {statCards.map(({ label, value, icon: Icon, color, bg, link }) => (
            <Link key={label} to={link}>
              <Card className="hover:shadow-md transition-all cursor-pointer hover:-translate-y-0.5">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div>
                      <p className="text-xs text-muted-foreground mb-1">{label}</p>
                      <p className="text-2xl font-bold">{loading ? '–' : value}</p>
                    </div>
                    <div className={`${bg} ${color} p-2 rounded-lg`}>
                      <Icon className="h-5 w-5" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Recent students */}
          <Card className="lg:col-span-2">
            <CardHeader className="flex flex-row items-center justify-between pb-3">
              <CardTitle className="text-base">最近新增學員</CardTitle>
              <Link to="/admin/clients">
                <Button variant="ghost" size="sm" className="text-xs gap-1">
                  全部 <ArrowRight className="h-3 w-3" />
                </Button>
              </Link>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="text-center py-6 text-muted-foreground text-sm">載入中...</div>
              ) : recentClients.length === 0 ? (
                <div className="text-center py-6 text-muted-foreground text-sm">
                  <Users className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  未有學員記錄
                </div>
              ) : (
                <div className="space-y-2">
                  {recentClients.map((c) => (
                    <div key={c.id} className="flex items-center justify-between py-2 border-b last:border-0">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-bold">
                          {c.name?.[0] || '?'}
                        </div>
                        <div>
                          <p className="text-sm font-medium">{c.name}</p>
                          <p className="text-xs text-muted-foreground">{c.course_type || '未設課程'} · {c.instructor || '未分配導師'}</p>
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        {c.created_at ? new Date(c.created_at).toLocaleDateString('zh-HK') : ''}
                      </p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick actions */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-base">快速操作</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              {quickActions.map(({ label, icon: Icon, path, color }) => (
                <Link key={path} to={path}>
                  <div className={`flex items-center gap-3 px-4 py-3 rounded-xl text-white text-sm font-medium cursor-pointer transition-colors ${color}`}>
                    <Icon className="h-4 w-4" />
                    {label}
                  </div>
                </Link>
              ))}

              <div className="pt-2 border-t mt-3">
                <p className="text-xs text-muted-foreground mb-2 font-medium">常用頁面</p>
                {[
                  { label: '導師管理', path: '/admin/instructors' },
                  { label: '網站設定', path: '/admin/settings' },
                  { label: '相片走馬燈', path: '/admin/gallery' },
                ].map(({ label, path }) => (
                  <Link key={path} to={path} className="flex items-center justify-between py-1.5 text-sm text-gray-600 hover:text-primary transition-colors">
                    <span>{label}</span>
                    <ArrowRight className="h-3 w-3" />
                  </Link>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
